import { onBeforeUnmount, ref } from 'vue'
import { sleep } from '@/utils/common.ts'

type IntervalId = ReturnType<typeof setInterval> | null

interface UseVisibilityCycleOptions {
  // Сколько ждать после возвращения на вкладку перед стартом
  delayBeforeStartMs?: number
  // Делать ли один вызов task сразу перед интервалом
  runImmediatelyOnStart?: boolean
  autoStart?: boolean
}

export function useVisibilityCycle(
  task: () => Promise<void> | void,
  intervalMs: number,
  options: UseVisibilityCycleOptions = {},
) {
  const { delayBeforeStartMs = 2000, runImmediatelyOnStart = true, autoStart = false } = options

  const intervalId = ref<IntervalId>(null)

  // токен чтобы отменять старые ожидания/гонки
  let seq = 0
  // защита от параллельного старта (пока await task)
  let starting = false

  function stop() {
    if (intervalId.value) {
      clearInterval(intervalId.value)

      intervalId.value = null
    }
  }

  function startInterval() {
    // перед стартом чистим старый
    stop()

    intervalId.value = setInterval(() => {
      // выполняем только если вкладка активна
      if (document.visibilityState !== 'visible') return

      void task()
    }, intervalMs)
  }

  async function handleVisible(currentSeq: number) {
    await sleep(delayBeforeStartMs)

    // если пока ждали было новое событие — выходим
    if (currentSeq !== seq) return

    // если уже не активная вкладка — выходим
    if (document.visibilityState !== 'visible') return

    // если уже запускаемся — выходим
    if (starting) return

    starting = true

    try {
      if (runImmediatelyOnStart) {
        await task()

        // пока ждали task, могли снова уйти
        if (currentSeq !== seq) return

        if (document.visibilityState !== 'visible') return
      }

      startInterval()
    } finally {
      starting = false
    }
  }

  function onVisibilityChange() {
    const currentSeq = ++seq

    if (document.visibilityState === 'hidden') {
      stop()

      return
    }

    void handleVisible(currentSeq)
  }

  document.addEventListener('visibilitychange', onVisibilityChange)

  // если сразу смонтировались на visible — стартанём по тем же правилам
  if (autoStart && document.visibilityState === 'visible') {
    onVisibilityChange()
  }

  onBeforeUnmount(() => {
    document.removeEventListener('visibilitychange', onVisibilityChange)

    stop()

    // инвалидация всех ожиданий
    seq++
  })

  return {
    start: () => onVisibilityChange(),
    stop,
    intervalId,
  }
}
